import React, { useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import ManageClientsModal from "common/components/campaign_clients/manage-clients-modal";
import {
  useAddCampaignClientMutation,
  useEditCampaignClientMutation,
  useDeleteCampaignClientMutation
} from "./campaigns-api-endpoints";
import "./campaign-clients-modal.styles.scss";

const campaignClientsReducer = (state, action) => {
  switch (action.type) {
    case "UPDATE_CLIENT_FIELD":
      return { ...state, client: { ...state.client, [action.payload.name]: action.payload.value } };
    case "BEFORE_ADD_CLIENT":
      return { ...state, errors: null, showEmailSent: false };
    case "AFTER_SUCCESSFUL_ADD_CLIENT":
      return {
        ...state,
        showEmailSent: true,
        client: {
          full_name: "",
          email: "",
          agent_name: action.payload.agentName,
          agent_email: action.payload.agentEmail,
          send_a_copy: true
        }
      };
    case "DISPLAY_ERRORS":
      return { ...state, errors: action.payload.errors };
    case "CLEAR_SHOW_EMAIL_SENT":
      return { ...state, showEmailSent: false };
    case "CLEAR_ERRORS":
      return { ...state, errors: null };
    case "SET_EDIT_CLIENT_ID":
      return {
        ...state,
        editClientId: action.payload.editClientId
      };
    default:
      console.warn(`Unknown action type: ${action.type}`);
      return state;
  }
};

const createDisplayErrors = errors => ({
  type: "DISPLAY_ERRORS",
  payload: { errors }
});

const propTypes = {
  toggleDisplay: PropTypes.func.isRequired,
  campaignGroup: PropTypes.shape({
    id: PropTypes.number,
    default_agent_name: PropTypes.string,
    default_agent_email: PropTypes.string,
    clients: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        full_name: PropTypes.string,
        email: PropTypes.string,
        agent_name: PropTypes.string,
        send_a_copy: PropTypes.bool
      })
    ),
    add_client_link: PropTypes.string,
    update_client_link: PropTypes.string,
    delete_client_link: PropTypes.string
  })
};

const CampaignClientsModal = ({
  toggleDisplay,
  campaignGroup = { default_agent_name: "", default_agent_email: "", clients: [] }
}) => {
  const {
    clients,
    default_agent_name: defaultAgentName,
    default_agent_email: defaultAgentEmail
  } = campaignGroup;

  const [state, dispatch] = useReducer(
    campaignClientsReducer,
    // initial state
    {
      client: {
        full_name: "",
        email: "",
        agent_name: defaultAgentName,
        agent_email: defaultAgentEmail,
        send_a_copy: true
      },
      errors: null,
      showEmailSent: false,
      editClientId: null
    }
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch({ type: "CLEAR_SHOW_EMAIL_SENT" });
    }, 3000);

    return () => {
      clearTimeout(timer);
    };
  }, [state.showEmailSent]);

  const handleClientUpdate = (name, value) => {
    dispatch({
      type: "UPDATE_CLIENT_FIELD",
      payload: { name, value }
    });
  };

  const [addCampaignClient, { isLoading: isAdding }] = useAddCampaignClientMutation();
  const addClient = async event => {
    event.preventDefault();
    dispatch({ type: "BEFORE_ADD_CLIENT" });
    try {
      await addCampaignClient({
        client: state.client,
        addClientUrl: campaignGroup.add_client_link,
        campaignGroupId: campaignGroup.id
      }).unwrap();
      dispatch({
        type: "AFTER_SUCCESSFUL_ADD_CLIENT",
        payload: { agentName: defaultAgentName, agentEmail: defaultAgentEmail }
      });
    } catch (error) {
      dispatch(createDisplayErrors(error.data));
    }
  };

  const [
    editCampaignClient,
    { isLoading: isUpdating, originalArgs: { client: { id: editingClientId } = {} } = {} }
  ] = useEditCampaignClientMutation();
  const handleEditClient = async (event, client) => {
    event.preventDefault();
    dispatch({ type: "CLEAR_ERRORS" });
    try {
      await editCampaignClient({
        client,
        editClientUrl: campaignGroup.update_client_link,
        campaignGroupId: campaignGroup.id
      }).unwrap();
      dispatch({ type: "SET_EDIT_CLIENT_ID", payload: { editClientId: null } });
    } catch (error) {
      dispatch(createDisplayErrors(error.data));
    }
  };

  const [
    deleteCampaignClient,
    { isLoading: isDeleting, originalArgs: { clientId: deletingClientId } = {} }
  ] = useDeleteCampaignClientMutation();
  const handleDeleteClient = async (event, clientId) => {
    event.preventDefault();
    dispatch({ type: "CLEAR_ERRORS" });
    try {
      await deleteCampaignClient({
        clientId,
        deleteClientUrl: campaignGroup.delete_client_link,
        campaignGroupId: campaignGroup.id
      }).unwrap();
    } catch (error) {
      dispatch(createDisplayErrors(error.data));
    }
  };

  const toggleClientEdit = clientId => {
    dispatch({
      type: "SET_EDIT_CLIENT_ID",
      payload: { editClientId: state.editClientId === clientId ? null : clientId }
    });
  };

  return (
    <ManageClientsModal
      toggleDisplay={toggleDisplay}
      errors={state.errors}
      client={state.client}
      clients={clients}
      addClient={addClient}
      handleClientUpdate={handleClientUpdate}
      handleEditClient={handleEditClient}
      handleDeleteClient={handleDeleteClient}
      toggleClientEdit={toggleClientEdit}
      editClientId={state.editClientId}
      deleteClientId={deletingClientId}
      isAdding={isAdding}
      isUpdating={isUpdating}
      isDeleting={isDeleting}
      showEmailSent={state.showEmailSent}
    />
  );
};

CampaignClientsModal.propTypes = propTypes;

export default CampaignClientsModal;
